import Box from "@mui/material/Box";
import { RefObject, useEffect, useRef, useState } from "react";
import { connectorLineMobileSx, connectorLineSx } from "./ConnectorLine.sx";

const CORNER_RADIUS = 24;

const generateMobilePath = (width: number, height: number): string => {
  const r = CORNER_RADIUS;
  const x = width - 0.5;
  const y = height;
  
  return `M0 0.5L${x - r} 0.5Q${x} 0.5 ${x} ${0.5 + r}L${x} ${y}`;
};

const generateDesktopPath = (width: number, height: number): string => {
  const r = CORNER_RADIUS;
  const midX = width * 0.42;
  const y = height - 0.5;
  
  return `M0 0.5L${midX - r} 0.5Q${midX} 0.5 ${midX} ${0.5 + r}V${y - r}Q${midX} ${y} ${midX + r} ${y}L${width} ${y}`;
};

export interface ConnectorLineProps {
  mobilePathRef?: RefObject<SVGPathElement | null>;
  desktopPathRef?: RefObject<SVGPathElement | null>;
  onPathsReady?: () => void;
}

export const ConnectorLine = ({
  mobilePathRef,
  desktopPathRef,
  onPathsReady,
}: ConnectorLineProps) => {
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const desktopContainerRef = useRef<HTMLDivElement>(null);
  const [mobilePath, setMobilePath] = useState("");
  const [desktopPath, setDesktopPath] = useState("");
  const [mobileSize, setMobileSize] = useState({ width: 0, height: 0 });
  const [desktopSize, setDesktopSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const mobileContainer = mobileContainerRef.current;
    const desktopContainer = desktopContainerRef.current;

    const updatePaths = () => {
      if (mobileContainer) {
        const { width, height } = mobileContainer.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setMobileSize({ width, height });
          setMobilePath(generateMobilePath(width, height));
        }
      }
      if (desktopContainer) {
        const { width, height } = desktopContainer.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setDesktopSize({ width, height });
          setDesktopPath(generateDesktopPath(width, height));
        }
      }
    };

    const resizeObserver = new ResizeObserver(updatePaths);
    
    if (mobileContainer) resizeObserver.observe(mobileContainer);
    if (desktopContainer) resizeObserver.observe(desktopContainer);

    updatePaths();

    return () => resizeObserver.disconnect();
  }, []);

  // Trigger animation when paths are rendered and refs are available
  useEffect(() => {
    if (!onPathsReady) return;
    
    const hasValidPath = mobilePathRef?.current || desktopPathRef?.current;
    if (hasValidPath) {
      // Double rAF to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onPathsReady();
        });
      });
    }
  }, [mobilePath, desktopPath, onPathsReady, mobilePathRef, desktopPathRef]);

  return (
    <>
      <Box ref={mobileContainerRef} sx={connectorLineMobileSx}>
        {mobilePath && (
          <svg
            width={mobileSize.width}
            height={mobileSize.height}
            fill="none"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            <path
              ref={mobilePathRef}
              d={mobilePath}
              fill="none"
              stroke="black"
              strokeWidth={1}
              strokeOpacity="0.2"
              strokeDasharray="9999"
              strokeDashoffset="9999"
              shapeRendering="geometricPrecision"
            />
          </svg>
        )}
      </Box>
      <Box ref={desktopContainerRef} sx={connectorLineSx}>
        {desktopPath && (
          <svg
            width={desktopSize.width}
            height={desktopSize.height}
            fill="none"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            <path
              ref={desktopPathRef}
              d={desktopPath}
              fill="none"
              stroke="black"
              strokeWidth={1}
              strokeOpacity="0.2"
              strokeDasharray="9999"
              strokeDashoffset="9999"
              shapeRendering="geometricPrecision"
            />
          </svg>
        )}
      </Box>
    </>
  );
};

export default ConnectorLine;
